import {
  CdkFixedSizeVirtualScroll,
  CdkVirtualForOf,
  CdkVirtualScrollViewport
} from '@angular/cdk/scrolling';
import { DatePipe } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  HostListener,
  Injector,
  OnDestroy,
  Signal,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
  viewChild
} from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import {
  Observable,
  Subject,
  debounceTime,
  filter,
  finalize,
  interval,
  map,
  of,
  startWith,
  switchMap,
  take,
  takeUntil,
  timeout
} from 'rxjs';

import { deliveryColor, pickupColor } from '@gfe/core/models/colors.model';
import { UserTemperatureV1Model } from '@gfe/core/models/entities/implementations/user-temperature-v1';
import {
  PlanFilter,
  PlanFilterOperator
} from '@gfe/core/models/entity/entity-selector-request.model';
import { buildPresetContext } from '@gfe/core/models/filter-preset.model';
import { SelectorsService } from '@gfe/core/services/selectors/selectors.service';
import { selectDataspaceState } from '@gfe/core/store/dataspace/dataspace.selectors';
import { selectPlan } from '@gfe/core/store/plan/plan.selectors';
import { OccupationSeries } from '@gfe/features/components/visualization/occupation/models/occupation.model';
import { ContextMenuOption } from '@gfe/shared/components/context-menu/context-menu.component';
import { TooltipComponent } from '@gfe/shared/components/tooltip/tooltip.component';
import { ContextMenuService } from '@gfe/shared/services/context-menu/context-menu.service';

import { ROUTE_ACTION_ENTITY_TYPE } from '@core/models/comments-v4.model';
import { FilterMethod } from '@core/models/data-visualization/data-visualization.state';
import {
  EntityModificationAction,
  EntityModificationOption
} from '@core/models/entities/entity-visualization-interface';
import { KNOWN_FEATURE_FLAGS } from '@core/models/feature-flags/known-feature-flags';
import { CommentsV4Service } from '@core/services/comments/comments-v4.service';
import { EntityLabelsTranslationService } from '@core/services/entity-labels-translation/entity-labels-translation.service';
import { EntityModificationService } from '@core/services/entity-modification/entity-modification.service';
import {
  CascadeResult,
  ModificationCascadeService
} from '@core/services/entity-modification/modification-cascade.service';
import { FeatureFlagsFacade } from '@core/services/feature-flags/feature-flags.facade';
import { ICONS } from '@core/services/icons/icons.service';
import { NotificationService } from '@core/services/notification/notification.service';
import { RegistrationRepairOutcome } from '@core/services/registration-repair/registration-repair.model';
import { RouteRegistrationRepairService } from '@core/services/registration-repair/route-registration-repair.service';
import { VisualFocusService } from '@core/services/visual-focus/visual-focus.service';
import { selectIsRouteActionCommentable } from '@core/store/comments-config/comments-config.selectors';
import { PopupDataVisualizationReplicator } from '@core/store/data-visualization-popup/data-visualization-popup.replicator';
import {
  applyTimelineModification,
  setFilterOnAggregator,
  setTimelineTimeStep,
  timelineModificationOutcome
} from '@core/store/data-visualization/data-visualization.actions';
import {
  selectAggregatorFilter,
  selectTimelineTimeStep
} from '@core/store/data-visualization/data-visualization.selectors';
import { PlanEditSessionActions } from '@core/store/plan-edit/plan-edit-session.actions';
import {
  selectIsPlanInViewHeld,
  selectPendingRouteUids,
  selectPlanEditSession,
  selectSessionPins,
  selectSessionRouteStarts,
  selectTouchedRouteUids
} from '@core/store/plan-edit/plan-edit-session.selectors';
import { UserPreferencesActions } from '@core/store/user-preferences/user-preferences.actions';
import { selectTimelinePreferences } from '@core/store/user-preferences/user-preferences.selectors';
import { getFormatedTimeFromDate } from '@core/utils/date-utils';
import { escapeHtml } from '@core/utils/html-escape.utils';
import { NEW_ROUTE_ROW } from '@core/utils/plan-edit/targets';
import { isNil } from '@core/utils/shared-utils';

import { CheckboxComponent } from '@shared/components/checkbox/checkbox.component';
import { CommentIndicatorComponent } from '@shared/components/comment-indicator/comment-indicator.component';
import { SelectItem } from '@shared/components/select/select-item.model';
import { SelectComponent } from '@shared/components/select/select.component';
import { TimelineStepPopupOutletComponent } from '@shared/components/timeline-step-popup-outlet/timeline-step-popup-outlet.component';
import { TimelineStepPopupService } from '@shared/services/timeline-step-popup/timeline-step-popup.service';

import { EntityAutoCompleteService } from '@features/components/entities/entity-auto-complete-dialog/entity-auto-complete.service';
import { PlanEditFormsService } from '@features/components/plan/edit/plan-edit-forms/plan-edit-forms.service';
import {
  RegistrationNameKind,
  ResourceKind,
  injectResourceNames
} from '@features/components/plan/edit/plan-edit-forms/resource-names';
import {
  CELL_HEIGHT,
  CELL_LEGEND_WIDTH,
  CELL_MIN_WIDTH,
  MIN_TASK_WIDTH_PX,
  TIME_STEP_DURATION_IN_MN,
  Task,
  TaskType,
  TimelineRow,
  TimelineState,
  initialTimelineState
} from '@features/components/visualization/timeline/models/timeline';
import { TimelineModificationActionDescriptor } from '@features/components/visualization/timeline/models/timeline-modification-descriptor';
import { TimelineModificationActionsService } from '@features/components/visualization/timeline/timeline-modification-actions.service';
import { TimelineUtils } from '@features/components/visualization/timeline/timeline.utils';

import { VisuItemLabel } from '../common/models/label.model';
import { altKeyLabelKey } from './edit/alt-key';
import {
  routeMenuEntries,
  routeMenuHeader,
  routeStartMenuEntries,
  routeStartMenuHeader,
  stopMenuEntries,
  stopMenuHeader
} from './edit/timeline-edit-menus';
import { TimelineEditOverlayComponent } from './edit/timeline-edit-overlay.component';
import { TimelineEditStripComponent } from './edit/timeline-edit-strip.component';
import { TimelineEditTargetsComponent } from './edit/timeline-edit-targets.component';
import { TimelineEditToursComponent } from './edit/timeline-edit-tours.component';
import { EditTarget, TimelineEditService } from './edit/timeline-edit.service';
import { TimelineGeometry } from './edit/timeline-geometry';

interface RouteChip {
  kind: ResourceKind;
  icon: string;
  name: string;
  /** The named registration the route holds for this resource. */
  registration: string | null;
  title: string;
}

const RESOURCE_CHIPS: {
  kind: ResourceKind;
  field: 'vehicle_uid' | 'driver_uid' | 'trailer_uid';
  registrationKind: RegistrationNameKind;
  registrationField:
    | 'vehicle_registration'
    | 'driver_registration'
    | 'trailer_registration';
  icon: string;
}[] = [
  {
    kind: 'vehicle',
    field: 'vehicle_uid',
    registrationKind: 'vehicle_registration',
    registrationField: 'vehicle_registration',
    icon: ICONS.truck
  },
  {
    kind: 'driver',
    field: 'driver_uid',
    registrationKind: 'driver_registration',
    registrationField: 'driver_registration',
    icon: ICONS.user
  },
  {
    kind: 'trailer',
    field: 'trailer_uid',
    registrationKind: 'trailer_registration',
    registrationField: 'trailer_registration',
    icon: ICONS.truckTrailer
  }
];

interface TaskTemperatures {
  temperatureUids: string[];
  rowIndex: number;
  taskIndex: number;
}

type TimelineDateContext = { date: string; width: string | null; left: string };

interface TaskTooltip {
  content: string;
  hint: string;
}

const TASK_DEFAULT_COLOR = '#656464';
const TASK_TYPE_TO_COLOR: Record<TaskType, string> = {
  basic: '#494141',
  pickup: pickupColor,
  delivery: deliveryColor,
  'delivery-pickup': '#984623ff'
};
const REASSIGN_MESSAGE_PREFIX = 'visualization.timeline.context_menu.vehicle_reassign_';

export const MODIFICATION_OUTCOME_TIMEOUT_MS = 30_000;

interface ModificationScope {
  dataSpaceId: number | undefined;
  planFilter: PlanFilter | undefined;
}

/** Preset-context surface for pickers opened from a timeline modification action. */
const TIMELINE_PRESET_SURFACE = 'TIMELINE';

/** The rows the timeline spans: a route without a stop occupies no time, so it moves no bound. */
function spannedRows(rows: TimelineRow[]): TimelineRow[] {
  const timed = rows.filter((row) => row.tasks.length > 0);
  return timed.length > 0 ? timed : rows;
}

@Component({
  selector: 'app-timeline',
  imports: [
    DatePipe,
    TranslatePipe,
    CdkVirtualScrollViewport,
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CheckboxComponent,
    TooltipComponent,
    CommentIndicatorComponent,
    TimelineStepPopupOutletComponent,
    SelectComponent,
    TimelineEditOverlayComponent,
    TimelineEditStripComponent,
    TimelineEditTargetsComponent,
    TimelineEditToursComponent
  ],
  providers: [TimelineEditService],
  templateUrl: './timeline.component.html',
  styleUrl: './timeline.component.css'
})
export class TimelineComponent implements AfterViewInit, OnDestroy {
  /** Services */
  private readonly entityLabelsTranslation = inject(EntityLabelsTranslationService);
  private readonly contextMenuService = inject(ContextMenuService);
  private readonly actions$ = inject(Actions);
  private readonly selectorsService = inject(SelectorsService);
  private readonly translate = inject(TranslateService);
  private readonly store = inject(Store);
  private readonly entityModificationService = inject(EntityModificationService);
  private readonly notificationService = inject(NotificationService);
  private readonly entityAutoCompleteService = inject(EntityAutoCompleteService);
  private readonly commentsApi = inject(CommentsV4Service);
  private readonly popupService = inject(TimelineStepPopupService);
  private readonly timelineModActionsService = inject(TimelineModificationActionsService);
  private readonly cascadeService = inject(ModificationCascadeService);
  private readonly registrationRepairService = inject(RouteRegistrationRepairService);
  private readonly replicator = inject(PopupDataVisualizationReplicator);
  private readonly injector = inject(Injector);
  protected readonly visualFocus = inject(VisualFocusService);
  protected readonly edit = inject(TimelineEditService);
  private readonly editForms = inject(PlanEditFormsService);

  private readonly aggregatorFilter$ = this.store.select(selectAggregatorFilter);

  protected readonly timesteps = [
    '5mn',
    '10mn',
    '15mn',
    '20mn',
    '30mn',
    '1h',
    '2h',
    '3h',
    '4h',
    '6h',
    '8h',
    '12h'
  ] as const;

  /** Subjects */
  private _stop$ = new Subject<void>();
  private _temperatureLoadRequest$ = new Subject<TaskTemperatures[]>();
  private readonly _commentBatchLoad$ = new Subject<{
    actionUids: string[];
    dataspaceId: number;
    planId?: string;
  }>();

  /** Utilities */
  protected readonly ICONS = ICONS;
  protected readonly taskColors = TASK_TYPE_TO_COLOR;
  protected readonly taskDefaultColor = TASK_DEFAULT_COLOR;
  protected readonly cellHeight = CELL_HEIGHT;
  protected readonly cellLegendWidth = CELL_LEGEND_WIDTH;

  /** Signals */
  private readonly timelineState = signal<TimelineState>(initialTimelineState);
  private readonly temperatureByUid = signal<Record<string, number>>({});
  protected readonly commentCountByActionUid = signal<Record<string, number>>({});
  protected readonly pendingRouteUids = this.store.selectSignal(selectPendingRouteUids);
  protected readonly editDrag = this.edit.drag;
  protected readonly editPlacing = this.edit.placing;
  /** What the per-block bindings read: these change only when a drag or a placing starts or ends. */
  protected readonly dragSourceUids = computed(
    () => new Set(this.editDrag()?.actionUids ?? this.editPlacing()?.actionUids ?? []),
    { equal: (a, b) => a.size === b.size && [...a].every((uid) => b.has(uid)) }
  );
  /** The row the dragged block is drawn in: its own, or the route it is moved over. */
  protected readonly carryRowUid = computed(() => {
    const drag = this.editDrag();
    return drag?.mode === 'move' ? drag.overRowUid : (drag?.routeUid ?? null);
  });
  /** The route a moved stop would land on, which lights up under it. */
  protected readonly dropRowUid = computed(() => {
    const drag = this.editDrag();
    return drag?.mode === 'move' && drag.targets.length > 0 ? drag.overRowUid : null;
  });
  /** The places a carried stop can land, by row: on every route while placing, else under it. */
  protected readonly editTargets = computed<Record<string, EditTarget[]>>(() => {
    const placing = this.editPlacing();
    if (placing) return placing.targets;
    const drag = this.editDrag();
    if (drag?.mode === 'reorder') return { [drag.routeUid]: drag.targets };
    if (drag?.mode === 'move' && drag.overRowUid)
      return { [drag.overRowUid]: drag.targets };
    return {};
  });
  protected readonly editHotTarget = computed(
    () => this.editPlacing()?.target ?? this.editDrag()?.target ?? null
  );
  protected readonly NEW_ROUTE_ROW = NEW_ROUTE_ROW;
  /**
   * What the + New route row says: what is carried, while a stop, a job or a tour is, and whether
   * a drop or a click there would open a route for it.
   */
  protected readonly newRouteSlot = computed(() => {
    const placing = this.editPlacing();
    const drag = this.editDrag();
    const carried =
      placing?.name ??
      (drag && drag.carrying !== 'start' && !drag.leaveBlocked ? drag.stopName : null);
    return { carried, hot: this.editHotTarget()?.routeUid === NEW_ROUTE_ROW };
  });

  /** The route a stop is reordered within: every other route dims, as no drop there reorders. */
  protected readonly reorderRouteUid = computed(() => {
    const drag = this.editDrag();
    return drag?.mode === 'reorder' ? drag.routeUid : null;
  });
  /** The route whose start is dragged: its marker stays behind, faded. */
  protected readonly startDragRouteUid = computed(() => {
    const drag = this.editDrag();
    return drag?.mode === 'start' ? drag.routeUid : null;
  });
  protected readonly altKey = altKeyLabelKey();
  private readonly editSession = this.store.selectSignal(selectPlanEditSession);
  /** The popup's store knows no session: the window holding the live actions refuses for it. */
  private readonly planHeld = this.store.selectSignal(selectIsPlanInViewHeld);
  private readonly sessionPins = this.store.selectSignal(selectSessionPins);
  /** The session's pins, while it edits the plan in view: action uids can repeat across plans. */
  protected readonly shownPins = computed(() => {
    const session = this.editSession();
    return session.mode !== 'off' && session.planId === this.plan()?.id
      ? this.sessionPins()
      : {};
  });
  private readonly sessionRouteStarts = this.store.selectSignal(selectSessionRouteStarts);
  /** The session's route starts, while it edits the plan in view: route uids can repeat across plans. */
  protected readonly shownRouteStarts = computed(() => {
    const session = this.editSession();
    return session.mode !== 'off' && session.planId === this.plan()?.id
      ? this.sessionRouteStarts()
      : {};
  });
  private readonly timelinePreferences = this.store.selectSignal(selectTimelinePreferences);
  private readonly isRouteActionCommentable = this.store.selectSignal(
    selectIsRouteActionCommentable
  );
  protected readonly showColorCodedBlocks = computed<boolean>(
    () => this.timelinePreferences().showColorCodedBlocks
  );
  protected readonly showTaskTypeColors = computed<boolean>(
    () => this.timelinePreferences().showTaskTypeColors
  );
  protected readonly showTemperatureBackground = computed<boolean>(
    () => this.timelinePreferences().showTemperatureBackground
  );
  protected readonly focusOnHover = computed<boolean>(
    () => this.timelinePreferences().focusOnHover
  );
  protected readonly isInitialized = signal<boolean>(false);
  protected readonly clickedTask = signal<Task | null>(null);
  protected readonly showExpandedTooltip = signal<boolean>(false);
  protected readonly timelineParentWidth = signal<number>(0);
  protected readonly timelineParentHeight = signal<number>(600);
  private readonly hasCalculatedOptimalTimeStep = signal<boolean>(false);
  private readonly sharedTimeStep = this.store.selectSignal(selectTimelineTimeStep);
  protected readonly nowDate = toSignal(
    interval(1000).pipe(
      startWith(new Date()),
      takeUntil(this._stop$),
      map(() => new Date())
    )
  ) as Signal<Date>;

  /** Store selectors */
  protected readonly dataSpaceState = this.store.selectSignal(selectDataspaceState);
  protected readonly plan = this.store.selectSignal(selectPlan);

  /** Properties */
  private clickedRowId: string | null = null;
  private resizeObserver: ResizeObserver | null = null;

  /** Input properties */
  readonly maxNbVisibleRows = input<number>(1000);
  readonly canFilterData = input<boolean>(false);
  readonly rows = input<TimelineRow[]>([]);
  readonly occupationSeries = input<OccupationSeries[]>([]);

  /** ViewChild references */
  readonly timelineParent = viewChild<ElementRef<HTMLDivElement>>('timelineParent');
  readonly contentViewport = viewChild<CdkVirtualScrollViewport>('contentViewport');

  /** Computed properties */
  protected readonly planId = computed<string | undefined>(() => this.plan()?.id);
  protected readonly dataspaceId = computed<number | undefined>(
    () => this.dataSpaceState().dataspace?.id
  );
  protected readonly columnWidth = computed<string>(() => `${this.widthPerTimeStep()}px`);
  protected readonly computedRows = computed<TimelineRow[]>(() => this.getAllRows());

  private readonly touchedRouteUids = this.store.selectSignal(selectTouchedRouteUids);
  private readonly registrationsEnabled = inject(FeatureFlagsFacade).isActive(
    KNOWN_FEATURE_FLAGS.ENABLE_LIVE_PLAN_EDIT_REGISTRATIONS
  );
  private readonly rowResources = computed(() => {
    if (!this.edit.editingHere()) return [];
    const withRegistrations = this.registrationsEnabled();
    return this.computedRows().flatMap((row) =>
      row.route
        ? RESOURCE_CHIPS.flatMap(({ kind, field, registrationKind, registrationField }) => [
            { kind, uid: row.route![field] ?? null },
            ...(withRegistrations
              ? [
                  {
                    kind: registrationKind,
                    uid: row.route![registrationField]?.code ?? null
                  }
                ]
              : [])
          ])
        : []
    );
  });
  private readonly resourceName = injectResourceNames(this.rowResources);

  /** Vehicle, driver and trailer of each shown route, while this tab edits the plan in view. */
  protected readonly routeChips = computed(() => {
    const chips = new Map<string, RouteChip[]>();
    if (!this.edit.editingHere()) return chips;
    this.currentLang();
    for (const row of this.computedRows()) {
      if (!row.route) continue;
      chips.set(
        row.uid,
        RESOURCE_CHIPS.flatMap(
          ({ kind, field, registrationKind, registrationField, icon }) => {
            const uid = row.route![field];
            if (!uid) return [];
            const code = this.registrationsEnabled()
              ? row.route![registrationField]?.code
              : undefined;
            const registration = code ? this.resourceName(registrationKind, code) : null;
            const name = this.resourceName(kind, uid);
            return [
              {
                kind,
                icon,
                name,
                registration,
                title: `${this.translate.instant(`features.plan.edit.route_label.${kind}`)}: ${name}${registration ? ` (${registration})` : ''}`
              }
            ];
          }
        )
      );
    }
    return chips;
  });

  protected isRouteFrozen(row: TimelineRow): boolean {
    return !!row.validated && this.edit.editingHere();
  }

  protected isRouteTouched(row: TimelineRow): boolean {
    return this.edit.editingHere() && this.touchedRouteUids().has(row.uid);
  }
  private readonly currentLang = toSignal(
    this.translate.onLangChange.pipe(map((event) => event.lang)),
    { initialValue: this.translate.getCurrentLang() }
  );
  /**
   * Collapsed tooltips of the rendered tasks, filled on first read. The template reads them
   * for every rendered task on every change-detection pass — at least once a second, since
   * `nowDate` ticks — so rebuilding them there means re-translating every label each time.
   * `computedRows` recreates the task objects, so a new map per row build keys on live tasks.
   */
  private readonly collapsedTooltipCache = computed(() => {
    this.currentLang();
    this.computedRows();
    return new WeakMap<Task, TaskTooltip>();
  });

  /**
   * The header is projected into the wrapper CDK translates down by the offset of the first
   * rendered row, and `sticky` resolves before that transform applies, so its `top` has to
   * cancel the offset to stay pinned to the viewport. The day lines and the now line hang off
   * the header for the same reason. CDK writes the transform and emits this offset in the same
   * change-detection pass, so the two never disagree on screen.
   */
  protected readonly headerTop = toSignal(
    toObservable(this.contentViewport).pipe(
      switchMap((viewport) => viewport?.renderedContentOffset ?? of(0)),
      map((offset) => `${-offset}px`)
    ),
    { initialValue: '0px' }
  );

  protected readonly nowTimeStepPosition = computed<string | null>(() =>
    this.getNowTimeStepPosition()
  );
  protected readonly nbOfRows = computed<number>(() =>
    Math.min(this.maxNbVisibleRows(), this.rows().length)
  );
  protected readonly contentHeight = computed<string>(
    () => `${this.nbOfRows() * CELL_HEIGHT}px`
  );
  protected readonly contentHeightPlusHeader = computed<string>(
    () => `${this.nbOfRows() * CELL_HEIGHT + 55}px`
  );
  protected readonly contentWidth = computed<string>(() => {
    const nbOfTimeSteps = this.timelineState().nbOfTimeSteps;
    const columnWidth = this.widthPerTimeStep();
    return `${nbOfTimeSteps * columnWidth}px`;
  });
  protected readonly nbOfTimeSteps = computed<number>(() =>
    this.timelineWidth() === 0 ? 0 : Math.floor(this.timelineWidth() / CELL_MIN_WIDTH)
  );
  protected readonly widthPerTimeStep = computed<number>(() => {
    if (!this.isInitialized()) {
      return 0;
    }
    const width = this.timelineWidth() / this.nbOfTimeSteps();
    if (width < CELL_MIN_WIDTH || isNaN(width)) {
      return CELL_MIN_WIDTH;
    }
    return width;
  });
  protected readonly columns = computed<string[]>(() => {
    if (!this.isInitialized()) return [];

    const nbOfTimeSteps = this.timelineState().nbOfTimeSteps;
    const columns: string[] = [];
    for (let i = 0; i < nbOfTimeSteps; i++) {
      const step = TimelineUtils.dateOfStep(
        i,
        this.timelineState().startDate,
        this.timelineState().nbTimeStepsPerDayPos
      );
      columns.push(
        step.toLocaleTimeString(this.translate.getCurrentLang(), {
          hour: '2-digit',
          minute: '2-digit'
        })
      );
    }
    return columns;
  });
  protected readonly bufferMultiplier = computed<number>(() => {
    const viewportHeight = this.timelineParentHeight();
    const itemsVisible = Math.ceil(viewportHeight / CELL_HEIGHT);
    return Math.max(2, Math.ceil(itemsVisible / 2));
  });
  protected readonly dates = computed<TimelineDateContext[]>(() => {
    if (!this.isInitialized()) return [];

    const nbOfTimeSteps = this.timelineState().nbOfTimeSteps;
    const columnWidthPx = this.widthPerTimeStep();
    const dates: TimelineDateContext[] = [];
    let prevDate = null;
    let prevTimeStep = 0;

    for (let i = 0; i < nbOfTimeSteps; i++) {
      const date = TimelineUtils.dateOfStep(
        i,
        this.timelineState().startDate,
        this.timelineState().nbTimeStepsPerDayPos
      );
      if (date.getDate() != prevDate) {
        prevDate = date.getDate();
        if (dates.length > 0) {
          dates[dates.length - 1].width = `${(i - prevTimeStep) * columnWidthPx}px`;
        }
        prevTimeStep = i;
        dates.push({
          date: date.toLocaleDateString(this.translate.getCurrentLang(), {
            year: 'numeric',
            month: 'short',
            day: '2-digit',
            weekday: 'short'
          }),
          width: null,
          left: `${i * columnWidthPx}px`
        });
      }
    }

    // Set width for last date
    if (dates.length > 0) {
      dates[dates.length - 1].width = `${(nbOfTimeSteps - prevTimeStep) * columnWidthPx}px`;
    }
    return dates;
  });
  protected readonly currentTimeStep = computed<number>(
    () => this.timelineState().nbTimeStepsPerDayPos
  );
  protected readonly timeStepItems = computed<SelectItem[]>(() =>
    this.timesteps.map((step, index) => ({ id: index, label: step }))
  );
  protected readonly timelineWidth = computed<number>(() => {
    if (this.timelineParentWidth() === 0) return 0;
    return this.timelineParentWidth() - CELL_LEGEND_WIDTH;
  });
  private readonly stepWidth = computed<number>(
    () => this.timelineWidth() / this.nbOfTimeSteps()
  );
  protected readonly geometry = computed<TimelineGeometry | null>(() => {
    if (!this.isInitialized() || this.nbOfTimeSteps() === 0) return null;
    const { startDate, endDate, nbTimeStepsPerDayPos } = this.timelineState();
    return {
      startDate,
      endDate,
      stepPos: nbTimeStepsPerDayPos,
      stepWidth: this.stepWidth()
    };
  });

  constructor() {
    this.edit.attach(this.geometry, inject(ElementRef).nativeElement, this.computedRows);

    effect(() => {
      if (!this.isInitialized() || this.nbOfTimeSteps() === 0) {
        return;
      }

      const temperatures = this.calculateTasksTemperatures();
      if (temperatures.length > 0) {
        this._temperatureLoadRequest$.next(temperatures);
      }
    });

    // Debounced temperature loading stream with request cancellation
    this._temperatureLoadRequest$
      .pipe(
        takeUntil(this._stop$),
        debounceTime(150), // Wait 150ms after last scroll
        switchMap((temperatures) => {
          // Filter to only uncached temperatures
          const uniqueUids = [
            ...new Set(temperatures.map((t) => t.temperatureUids.flat()).flat())
          ];
          const uncachedUids = uniqueUids.filter((uid) =>
            isNil(this.temperatureByUid()[uid])
          );

          if (uncachedUids.length === 0) {
            // All temperatures cached, no API call needed
            return [];
          }

          // Load only uncached temperatures
          return this.loadTemperatures(uncachedUids);
        })
      )
      .subscribe();

    this._commentBatchLoad$
      .pipe(
        takeUntil(this._stop$),
        debounceTime(200),
        switchMap(({ actionUids, dataspaceId, planId }) =>
          this.commentsApi.getConversations$({
            entityType: ROUTE_ACTION_ENTITY_TYPE,
            entityUids: actionUids,
            dataspaceId,
            planId,
            includeResolved: true
          })
        )
      )
      .subscribe({
        next: (conversations) => {
          const counts: Record<string, number> = {};
          for (const conv of conversations) {
            counts[conv.entityUid] = (counts[conv.entityUid] ?? 0) + conv.comments.length;
          }
          this.commentCountByActionUid.set(counts);
        },
        error: () => {}
      });

    effect(() => {
      const actionUids = [
        ...new Set(
          this.rows()
            .flatMap((row) => row.tasks.map((t) => t.actionUid))
            .filter((uid): uid is string => !!uid)
        )
      ];
      const dataspaceId = this.dataspaceId();
      const planId = this.planId();

      if (!actionUids.length || dataspaceId === undefined) return;
      if (!this.isRouteActionCommentable()) return;
      this._commentBatchLoad$.next({ actionUids, dataspaceId, planId });
    });

    effect(() => {
      const rows = this.rows();
      const maxRows = this.maxNbVisibleRows();
      const initialized = this.isInitialized();
      const width = this.timelineWidth();

      if (!initialized || width === 0) return;

      const sharedTimeStep = untracked(() => this.sharedTimeStep());
      const currentTimeStep = untracked(() => this.currentTimeStep());
      const hasCalculated = untracked(() => this.hasCalculatedOptimalTimeStep());

      let timeStep: number;
      if (!isNil(sharedTimeStep)) {
        timeStep = sharedTimeStep;
      } else if (!hasCalculated) {
        timeStep = this.getOptimalTimeStep(rows, width);
        this.hasCalculatedOptimalTimeStep.set(true);
      } else {
        timeStep = currentTimeStep || 4;
      }

      this.renderTimelineData(rows, timeStep, maxRows);

      // A step nobody shared was fitted to this window's own width, so a window opening
      // later — the popup is 800px wide against a full-screen main window — would fit a
      // much coarser one from the same rows. Publishing it lets the first window to render
      // settle the step for every window that follows.
      if (isNil(sharedTimeStep)) {
        this.selectTimeStep(timeStep);
      }
    });

    effect(() => {
      const sharedTimeStep = this.sharedTimeStep();
      const initialized = this.isInitialized();

      if (isNil(sharedTimeStep) || !initialized) return;

      untracked(() => {
        if (this.timelineState().rows.length === 0) return;
        if (this.currentTimeStep() === sharedTimeStep) return;

        this.updateTimeStep(sharedTimeStep);
      });
    });

    this.aggregatorFilter$.pipe(takeUntil(this._stop$)).subscribe((data) => {
      if (data.filterMethod === FilterMethod.DISABLED) {
        this.clickedRowId = null;
      }
    });

    effect(() => {
      const routeUid = this.visualFocus.pinnedRouteUid();
      if (isNil(routeUid)) return;

      // Untracked: the row list is large and changes for reasons of its own; only the pin
      // should move the viewport.
      untracked(() => {
        // Only for a pin raised somewhere else — a click on the map. A row this timeline
        // pinned itself was under the pointer when it was clicked, so there is nothing to
        // scroll to, and scrolling anyway pulls the list out from under that pointer: whichever
        // row lands there next raises a hover nobody asked for.
        if (routeUid === this.clickedRowId) return;

        const index = this.computedRows().findIndex((row) => row.uid === routeUid);
        if (index < 0) return;
        this.contentViewport()?.scrollToIndex(index, 'smooth');
      });
    });
  }

  ngAfterViewInit() {
    if (isNil(this.timelineParent())) return;

    const tlP = this.timelineParent()!;
    const { offsetWidth, offsetHeight } = tlP.nativeElement;

    this.resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        const width = entry.contentRect.width;
        const height = entry.contentRect.height;
        this.timelineParentWidth.set(width);
        this.timelineParentHeight.set(height);

        requestAnimationFrame(() => {
          this.contentViewport()?.checkViewportSize();
        });
      }
    });
    this.resizeObserver.observe(tlP.nativeElement);

    this.timelineParentWidth.set(offsetWidth);
    this.timelineParentHeight.set(offsetHeight);

    this.registerContextMenu();
    this.listenToModificationCommands();

    this.isInitialized.set(true);
  }

  ngOnDestroy() {
    this._stop$.next();
    this._stop$.complete();
    // Removing an element under the pointer does not fire its `mouseleave`, and the focus
    // service outlives this component, so the hover has to be dropped here or it is painted
    // onto whatever visualization opens next.
    this.visualFocus.clearHover();
    this.isInitialized.set(false);
    this.hasCalculatedOptimalTimeStep.set(false);

    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }

  onMouseClickOnRow(row: TimelineRow) {
    if (!this.canFilterData()) return;

    const shouldDisableFilter = !row.uid || this.clickedRowId === row.uid;

    if (shouldDisableFilter) {
      this.clickedRowId = null;
      this.store.dispatch(
        setFilterOnAggregator({
          filterMethod: FilterMethod.DISABLED,
          filteredAggregatorIds: []
        })
      );
    } else {
      this.clickedRowId = row.uid;
      this.store.dispatch(
        setFilterOnAggregator({
          filterMethod: FilterMethod.BY_AGGREGATOR,
          filteredAggregatorIds: [String(row.uid)]
        })
      );
    }
  }

  /**
   * A route's own tier. `soft` here means an *unrelated* route the focus leaves as faint
   * context — `ResolvedFocus.otherRoutes` — so it maps to a fade, never to the untouched tier.
   *
   * One binding per element rather than one per state: this runs for every rendered block on
   * every change-detection tick, and the timeline already ticks on every mouse event.
   */
  protected focusClassForRow(routeUid: string): string {
    const state = this.visualFocus.routeState(routeUid);
    return state === 'dim' ? 'focus-dim' : state === 'soft' ? 'focus-soft' : '';
  }

  /**
   * An action's own tier. `soft` here is the opposite of the row's: it means an action
   * *related* to the focused one through a shared job, which is left exactly as it was rather
   * than faded. The two states share a name in `VisualFocusService`, so resolving both through
   * one method would fade the siblings the focus exists to surface.
   *
   * Synthetic start/finish tasks stand for no action, so they follow their route.
   */
  protected focusClassForTask(routeUid: string, task: Task): string {
    if (isNil(task.actionUid)) return this.focusClassForRow(routeUid);
    return this.visualFocus.actionState(task.actionUid) === 'dim' ? 'focus-dim' : '';
  }

  /**
   * Clears the action pin when the click lands on the timeline itself rather than on a row,
   * a task, or the row legend — all three sit inside `.timeline-row` and handle their own
   * click. Only the action focus is cleared: a background click must leave an active route
   * filter alone, or it would take the stop sequence numbers down with it.
   */
  protected onTimelineBackgroundClick(event: MouseEvent): void {
    const target = event.target as HTMLElement;
    if (target.closest('.timeline-row')) return;
    this.visualFocus.clearJobPin();
  }

  protected onRowHover(routeUid: string | null): void {
    this.visualFocus.hoverRoute(routeUid);
  }

  protected onTaskHover(actionUid: string | undefined | null): void {
    this.visualFocus.hoverAction(actionUid ?? null);
  }

  protected onTaskClick(task: Task, event: MouseEvent | KeyboardEvent) {
    event.stopPropagation();

    if (task.actionUid) {
      this.visualFocus.toggleJobPin(task.actionUid);
    }

    // Step tasks open the comment/details popup anchored to the clicked block,
    // replacing the old click-to-expand tooltip.
    if (task.actionUid) {
      this.openStepPopup(task, event.currentTarget as HTMLElement);
      return;
    }

    // Non-step tasks (synthetic start/finish markers) keep the expandable tooltip.
    if (this.clickedTask() === task) {
      this.showExpandedTooltip.update((expanded) => !expanded);
    } else {
      this.clickedTask.set(task);
      this.showExpandedTooltip.set(true);
    }
  }

  protected onClickOutside() {
    this.clickedTask.set(null);
    this.showExpandedTooltip.set(false);
  }

  /** True when the step popup is currently open for this task (drives the selection ring). */
  protected isTaskSelected(task: Task): boolean {
    return this.popupService.state()?.data === task;
  }

  /**
   * The ring means "pinned" and nothing else. The step popup and a job pin are both pins, so
   * they share it. A pinned route is not one: it lights every action of the row, and a ring on
   * all twenty blocks says "this route is filtered" rather than "these are this job's legs".
   *
   * Read off the pin and not off the resolved focus, which a hover takes over: the ring is
   * what still says which blocks are pinned while the pointer is inspecting something else.
   */
  protected isTaskRinged(task: Task): boolean {
    if (this.isTaskSelected(task)) return true;
    if (isNil(task.actionUid)) return false;

    return this.visualFocus.pinnedJobActionUids().has(task.actionUid);
  }

  /**
   * Opens (or toggles) the step popup for the given task, anchored to `anchorEl`.
   * After opening, the conversation is fetched from the API via refreshConversation.
   */
  protected openStepPopup(task: Task, anchorEl: HTMLElement): void {
    if (!task.actionUid) return;

    // Toggle: close if already open for this anchor element.
    if (this.popupService.isOpenFor(anchorEl)) {
      this.popupService.close();
      return;
    }

    this.popupService.open(anchorEl, task);
    this.refreshConversation(task);
  }

  /** Fetches the conversation for the given task, updates the popup service and badge count. */
  private refreshConversation(task: Task): void {
    this.commentsApi
      .getConversations$({
        entityType: ROUTE_ACTION_ENTITY_TYPE,
        entityUids: [task.actionUid!],
        dataspaceId: this.dataspaceId(),
        planId: this.planId(),
        includeResolved: true
      })
      .pipe(takeUntil(this._stop$))
      .subscribe({
        next: (conversations) => {
          const conv = conversations[0] ?? null;
          this.popupService.setConversation(conv);
          this.commentCountByActionUid.update((m) => ({
            ...m,
            [task.actionUid!]: conv?.comments.length ?? 0
          }));
        },
        error: () => {
          this.popupService.setConversation(null);
          this.notificationService.error(
            this.translate.instant('visualization.timeline.step_detail.load_error_title'),
            this.translate.instant('visualization.timeline.step_detail.load_error_message')
          );
        }
      });
  }

  protected onAddComment(text: string): void {
    const task = this.popupService.state()?.data;
    if (!task?.actionUid) return;

    this.popupService.setSaving(true);
    this.commentsApi
      .postComment$(
        {
          entityType: ROUTE_ACTION_ENTITY_TYPE,
          entityUid: task.actionUid,
          planId: this.planId(),
          message: text,
          mentions: []
        },
        this.dataspaceId()
      )
      .pipe(takeUntil(this._stop$))
      .subscribe({
        next: () => {
          this.popupService.setSaving(false);
          this.refreshConversation(task);
        },
        error: () => {
          this.popupService.setSaving(false);
          this.notificationService.error(
            this.translate.instant('visualization.timeline.step_detail.save_error_title'),
            this.translate.instant('visualization.timeline.step_detail.save_error_message')
          );
        }
      });
  }

  protected onEditComment(edit: { id: string; message: string }): void {
    const task = this.popupService.state()?.data;
    if (!task?.actionUid) return;

    this.popupService.setSaving(true);
    this.commentsApi
      .editComment$(edit.id, edit.message, this.dataspaceId())
      .pipe(takeUntil(this._stop$))
      .subscribe({
        next: () => {
          this.popupService.setSaving(false);
          this.refreshConversation(task);
        },
        error: () => {
          this.popupService.setSaving(false);
          this.notificationService.error(
            this.translate.instant('visualization.timeline.step_detail.save_error_title'),
            this.translate.instant('visualization.timeline.step_detail.save_error_message')
          );
        }
      });
  }

  protected onRemoveComment(commentId: string): void {
    const task = this.popupService.state()?.data;
    if (!task?.actionUid) return;

    this.popupService.setSaving(true);
    this.commentsApi
      .deleteComment$(commentId, this.dataspaceId())
      .pipe(takeUntil(this._stop$))
      .subscribe({
        next: () => {
          this.popupService.setSaving(false);
          this.refreshConversation(task);
        },
        error: () => {
          this.popupService.setSaving(false);
          this.notificationService.error(
            this.translate.instant('visualization.timeline.step_detail.save_error_title'),
            this.translate.instant('visualization.timeline.step_detail.save_error_message')
          );
        }
      });
  }

  protected onToggleResolve(): void {
    const conversation = this.popupService.conversation();
    const conversationId = conversation?.id;
    if (!conversationId) return;

    const resolved = conversation!.resolved;
    this.popupService.setSaving(true);
    this.commentsApi
      .setResolved$(conversationId, !resolved, this.dataspaceId())
      .pipe(takeUntil(this._stop$))
      .subscribe({
        next: (updated) => {
          this.popupService.setSaving(false);
          this.popupService.setConversation(updated);
        },
        error: () => {
          this.popupService.setSaving(false);
          this.notificationService.error(
            this.translate.instant('visualization.timeline.step_detail.save_error_title'),
            this.translate.instant('visualization.timeline.step_detail.save_error_message')
          );
        }
      });
  }

  @HostListener('click', ['$event']) onComponentClick(event: MouseEvent) {
    // Check if click is outside of task elements
    const target = event.target as HTMLElement;
    if (!target.closest('.timeline-task')) {
      this.onClickOutside();
    }
  }

  @HostListener('wheel', ['$event']) onWheel(event: WheelEvent) {
    if (!event.metaKey && !event.ctrlKey) {
      return;
    }

    event.preventDefault();

    const currentIndex = this.currentTimeStep();

    const direction = event.deltaY > 0 ? 1 : -1;
    const newIndex = currentIndex + direction;

    if (newIndex >= 0 && newIndex < this.timesteps.length) {
      this.selectTimeStep(newIndex);
    }
  }

  protected onTimeStepChange(value: number) {
    if (value < 0 || value >= this.timesteps.length) {
      return;
    }

    this.selectTimeStep(value);
  }

  private selectTimeStep(timeStep: number) {
    this.store.dispatch(setTimelineTimeStep({ timeStep }));
  }

  protected toggleColorCodedBlocks = (checked: boolean) => {
    this.store.dispatch(
      UserPreferencesActions.setTimelinePreferences({
        preferences: { showColorCodedBlocks: checked }
      })
    );
  };

  protected toggleTaskTypeColors = (checked: boolean) => {
    this.store.dispatch(
      UserPreferencesActions.setTimelinePreferences({
        preferences: { showTaskTypeColors: checked }
      })
    );
  };

  protected toggleTemperatureBackground = (checked: boolean) => {
    this.store.dispatch(
      UserPreferencesActions.setTimelinePreferences({
        preferences: { showTemperatureBackground: checked }
      })
    );
  };

  protected toggleFocusOnHover = (checked: boolean) => {
    this.store.dispatch(
      UserPreferencesActions.setTimelinePreferences({
        preferences: { focusOnHover: checked }
      })
    );
  };

  protected getRowTooltipContent(row: TimelineRow): string {
    // In edit mode the frozen notice already says the route is validated, and how to edit it.
    const validation = this.isRouteFrozen(row)
      ? [escapeHtml(this.translate.instant('features.plan.edit.route_label.frozen'))]
      : row.validated
        ? [
            `<strong>${escapeHtml(this.translate.instant('route_validation.validated_tooltip'))}</strong>`
          ]
        : [];
    const labels = row.labels?.length
      ? this.entityLabelsTranslation
          .translateMarkerLabels(row.labels)
          .map((label) => `<strong>${label.label}:</strong> ${label.value}`)
      : [];
    return [...validation, ...labels].join('<br>');
  }

  protected getTooltipTitle(task: Task): string {
    return task.name;
  }

  protected getTooltipContent(task: Task): string {
    return this.taskTooltip(task).content;
  }

  protected getTooltipHint(task: Task): string {
    return this.taskTooltip(task).hint;
  }

  /** What a stop that can be dragged does under each gesture, under its usual tooltip. */
  protected readonly editGesturesHint = computed(() => {
    this.currentLang();
    const t = (key: string) => escapeHtml(this.translate.instant(key));
    const line = 'flex items-center gap-1.5';
    return (
      `<div class="grid gap-1">` +
      `<div class="${line}"><i class="${ICONS.handGrabbing}"></i>${t('features.plan.edit.gestures.drag_to_pin')}</div>` +
      `<div class="${line}"><kbd class="key-chip border-transparent">${t(this.altKey)}</kbd>${t('features.plan.edit.gestures.to_reorder')}</div>` +
      `<div class="${line}"><i class="${ICONS.mouseRightClick}"></i>${t('features.plan.edit.gestures.right_click')}</div>` +
      `</div>`
    );
  });

  /** What a route's start marker does under each gesture, in edit mode. */
  protected readonly startGesturesHint = computed(() => {
    this.currentLang();
    const t = (key: string) => escapeHtml(this.translate.instant(key));
    const line = 'flex items-center gap-1.5';
    return (
      `<div class="grid gap-1">` +
      `<div class="${line}"><i class="${ICONS.handGrabbing}"></i>${t('features.plan.edit.gestures.drag_start')}</div>` +
      `<div class="${line}"><i class="${ICONS.mouseRightClick}"></i>${t('features.plan.edit.gestures.right_click')}</div>` +
      `</div>`
    );
  });

  /** The clicked task is built fresh, as it alone depends on the expand toggle. */
  private taskTooltip(task: Task): TaskTooltip {
    if (this.clickedTask() === task) {
      const expanded = this.showExpandedTooltip();
      return {
        content: this.buildTooltipContent(task, expanded),
        hint: expanded ? 'Click again to collapse' : 'Click to expand'
      };
    }

    const cache = this.collapsedTooltipCache();
    let tooltip = cache.get(task);
    if (isNil(tooltip)) {
      tooltip = {
        content: this.buildTooltipContent(task, false),
        hint: task.labels.some((label) => label.level > 0)
          ? 'Click to see more details'
          : ''
      };
      cache.set(task, tooltip);
    }
    return tooltip;
  }

  private buildTooltipContent(task: Task, expanded: boolean): string {
    const labels = this.entityLabelsTranslation.translateMarkerLabels(
      expanded ? task.labels : task.labels.filter((label) => label.level === 0)
    );
    const timeRange = `${getFormatedTimeFromDate(task.startDate)} - ${getFormatedTimeFromDate(task.endDate)}`;

    if (labels.length === 0) {
      return timeRange;
    }

    const groupedLabels = labels.reduce(
      (acc, label) => {
        if (!acc[label.level]) acc[label.level] = [];
        acc[label.level].push(label);
        return acc;
      },
      {} as Record<number, VisuItemLabel[]>
    );

    const content = [timeRange, ''];
    Object.keys(groupedLabels)
      .sort((a, b) => parseInt(a) - parseInt(b))
      .forEach((level, index) => {
        if (index > 0) content.push('');
        groupedLabels[parseInt(level)].forEach((label) => {
          content.push(`<strong>${label.label}:</strong> ${label.value}`);
        });
      });

    return content.join('<br>');
  }

  protected trackTimeStep(index: number, item: string): string {
    return `${item}_${index}`;
  }

  protected trackTimelineColumn(index: number, item: string): string {
    return `column_${item}_${index}`;
  }

  protected trackTimelineRow(index: number, item: TimelineRow): string {
    return `${item.uid}_${item.name}_${index}`;
  }

  protected trackTimelineDateContext(index: number, item: TimelineDateContext): string {
    return `${item.date}_${item.left}_${index}`;
  }

  protected trackTask(index: number, item: Task): string {
    return `${item.id}_${item.name}_${item.startDate.getTime()}_${item.endDate.getTime()}_${index}`;
  }

  protected isBlockTooSmall(width: number | undefined): boolean {
    return (width ?? 0) < 40;
  }

  protected truncateTaskName(task: Task, maxLength: number = 4): string {
    return task.name.length <= maxLength ? task.name : task.siteUid.substring(0, maxLength);
  }

  protected getTaskTypeToColor(taskType: TaskType): string {
    return TASK_TYPE_TO_COLOR[taskType];
  }

  private calculateTasksTemperatures(): TaskTemperatures[] {
    const temperatures: TaskTemperatures[] = [];
    const { rows } = this.timelineState();
    const cachedTemperatures = this.temperatureByUid();

    for (let i = 0; i < this.nbOfRows(); i++) {
      const row = rows[i];
      if (isNil(row?.tasks)) continue;

      for (let j = 0; j < row.tasks.length; j++) {
        const task = row.tasks[j];
        if (
          !isNil(task.temperatureUids) &&
          task.temperatureUids.some((uid) => isNil(cachedTemperatures[uid]))
        ) {
          temperatures.push({
            rowIndex: i,
            taskIndex: j,
            temperatureUids: task.temperatureUids
          });
        }
      }
    }

    return temperatures;
  }

  private getAllRows(): TimelineRow[] {
    if (!this.isInitialized() || this.nbOfTimeSteps() === 0) {
      return [];
    }

    const { rows, nbTimeStepsPerDayPos, startDate, endDate } = this.timelineState();
    const temperatures = this.temperatureByUid();
    const visibleRows: TimelineRow[] = [];
    const stepWidth = this.stepWidth();

    for (let i = 0; i < this.nbOfRows(); i++) {
      const row = rows[i];
      if (isNil(row?.tasks)) continue;

      const tasks: Task[] = [];

      for (let j = 0; j < row.tasks.length; j++) {
        const taskStartTimeStep = TimelineUtils.dateToStep(
          row.tasks[j].startDate,
          startDate,
          endDate,
          nbTimeStepsPerDayPos
        );
        const taskEndTimeStep = TimelineUtils.dateToStep(
          row.tasks[j].endDate,
          startDate,
          endDate,
          nbTimeStepsPerDayPos
        );

        const visibleTimeSteps = Math.max(0, taskEndTimeStep - taskStartTimeStep);
        const isMarker = ['start', 'finish'].includes(row.tasks[j].kind);
        const _width = isMarker
          ? visibleTimeSteps * stepWidth
          : Math.max(MIN_TASK_WIDTH_PX, visibleTimeSteps * stepWidth);
        const _leftPos = taskStartTimeStep * stepWidth;

        const isNonServiceTask = [
          'pause',
          'home-rest',
          'wait',
          'setup',
          'setdown'
        ].includes(row.tasks[j].kind);
        tasks.push({
          ...row.tasks[j],
          _width,
          _leftPos,
          lazyProps: {
            isDeliveryPickup: row.tasks[j].type === 'delivery-pickup',
            lineColor: undefined,
            color: isNonServiceTask ? '#656464' : this.getTaskTypeToColor(row.tasks[j].type)
          }
        });
      }

      visibleRows.push({ ...row, tasks });
    }

    this.applyTemperaturesToTasks(visibleRows, temperatures);

    return visibleRows;
  }

  private applyTemperaturesToTasks(
    visibleRows: TimelineRow[],
    temperatures: Record<string, number>
  ): void {
    for (const row of visibleRows) {
      if (isNil(row?.tasks)) continue;

      for (let j = 0; j < row.tasks.length; j++) {
        const task = row.tasks[j];

        // Apply color to temperature segments if they exist - create new array to avoid mutation issues
        if (task.temperatureSegments) {
          task.temperatureSegments = task.temperatureSegments.map((segment) => {
            const tempCelsius = temperatures[segment.temperatureUid];
            return {
              ...segment,
              color: !isNil(tempCelsius)
                ? TimelineUtils.temperatureToColor(tempCelsius)
                : segment.color
            };
          });
        }

        if (task.lazyProps && task.temperatureUids && task.temperatureUids.length > 0) {
          for (const temperature of task.temperatureUids) {
            // Skip only this uid: it is still being fetched, and abandoning the walk here
            // would leave every later row and task of the timeline uncoloured.
            if (isNil(temperatures[temperature])) {
              continue;
            }
            task.lazyProps.lineColor = TimelineUtils.temperatureToColor(
              temperatures[temperature]
            );
          }
        }
      }
    }
  }

  private renderTimelineData(
    rows: TimelineRow[],
    nbTimeStepsPerDayPos: number,
    maxNbVisibleRows: number
  ) {
    const timeStepDurationInMs = TIME_STEP_DURATION_IN_MN[nbTimeStepsPerDayPos] * 60 * 1000;
    const spanned = spannedRows(rows);

    const firstDateTime = spanned.reduce(
      (t, row) => Math.min(t, row.startDate.getTime()),
      1e14
    );
    const dataStartDate = new Date(firstDateTime);
    const startDate = new Date(
      TimelineUtils.floorDate(dataStartDate, nbTimeStepsPerDayPos).getTime() -
        timeStepDurationInMs
    );

    const lastDateTime = spanned.reduce((t, row) => Math.max(t, row.endDate.getTime()), 0);
    const dataEndDate = new Date(lastDateTime);
    const endDate = new Date(
      TimelineUtils.floorDate(dataEndDate, nbTimeStepsPerDayPos).getTime() +
        timeStepDurationInMs
    );

    const periodDurationInMs = endDate.getTime() - startDate.getTime();
    const nbOfTimeSteps = Math.ceil(periodDurationInMs / timeStepDurationInMs);
    const nbOfVisibleRows = Math.min(maxNbVisibleRows, rows.length);

    this.timelineState.update((state) => ({
      ...state,
      dataStartDate,
      dataEndDate,
      startDate,
      endDate,
      rows,
      nbOfVisibleRows,
      nbOfTimeSteps,
      nbTimeStepsPerDayPos
    }));
  }

  private updateTimeStep(timeStep: number) {
    timeStep = Math.max(0, Math.min(timeStep, this.timesteps.length - 1));

    const { dataStartDate, dataEndDate } = this.timelineState();
    const timeStepDurationInMs = TIME_STEP_DURATION_IN_MN[timeStep] * 60 * 1000;

    const newStartDate = new Date(
      TimelineUtils.floorDate(dataStartDate, timeStep).getTime() - timeStepDurationInMs
    );
    const newEndDate = new Date(
      TimelineUtils.ceilDate(dataEndDate, timeStep).getTime() + timeStepDurationInMs
    );

    const nbOfTimeSteps = Math.ceil(
      (newEndDate.getTime() - newStartDate.getTime()) / timeStepDurationInMs
    );

    this.timelineState.update((state) => ({
      ...state,
      startDate: newStartDate,
      endDate: newEndDate,
      nbTimeStepsPerDayPos: timeStep,
      nbOfTimeSteps
    }));
  }

  private registerContextMenu(): void {
    this.contextMenuService.register(
      'timeline-row-label',
      (target) => {
        const rowUid = target.getAttribute('data-row-uid');

        const staticOptions: ContextMenuOption[] = [
          {
            icon: ICONS.copy,
            label: this.translate.instant('common.copy_name'),
            action: () => {
              const label = target.getAttribute('data-label');
              if (label) navigator.clipboard.writeText(label);
            }
          },
          {
            icon: ICONS.copy,
            label: this.translate.instant('common.copy_uid'),
            action: () => {
              if (rowUid) navigator.clipboard.writeText(rowUid);
            }
          },
          {
            icon: ICONS.funnel,
            label: this.translate.instant('common.filter'),
            action: () => {
              if (rowUid) {
                this.store.dispatch(
                  setFilterOnAggregator({
                    filterMethod: FilterMethod.BY_AGGREGATOR,
                    filteredAggregatorIds: [rowUid]
                  })
                );
              }
            }
          }
        ];

        if (this.edit.editingHere()) {
          const row = this.computedRows().find((candidate) => candidate.uid === rowUid);
          return [
            ...staticOptions,
            ...(row ? this.buildRouteEditEntries(row, target) : [])
          ];
        }
        if (this.planHeld()) return staticOptions;

        const modificationOptions = rowUid ? this.buildModificationOptions(rowUid) : [];

        return [...staticOptions, ...modificationOptions];
      },
      (target) => {
        const row = this.computedRows().find(
          (candidate) => candidate.uid === target.getAttribute('data-row-uid')
        );
        return row ? routeMenuHeader(row, this.translateFn) : null;
      }
    );

    this.contextMenuService.register(
      'timeline-task',
      (target) => [
        ...this.buildTaskMenuOptions(target),
        ...this.buildStopEditEntries(target)
      ],
      (target) => {
        const stop = this.findStop(target.getAttribute('data-action-uid'));
        return stop ? stopMenuHeader(stop.task, this.translateFn) : null;
      }
    );

    this.contextMenuService.register(
      'timeline-start-marker',
      (target) => {
        const start = this.findEditableStart(target);
        if (!start) return [];
        return routeStartMenuEntries({
          ...start,
          starts: this.shownRouteStarts(),
          dispatch: (gesture) =>
            this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture })),
          openForm: (request) => this.editForms.open(target, request),
          translate: this.translateFn
        });
      },
      (target) => {
        const start = this.findEditableStart(target);
        return start ? routeStartMenuHeader(start.row, start.task, this.translateFn) : null;
      }
    );
  }

  /** Opens the new route form with nothing to move into it, anchored to what was clicked. */
  protected openNewRoute(ev: MouseEvent): void {
    this.editForms.open(ev.currentTarget as HTMLElement, { kind: 'new-route', then: null });
  }

  /** A route's start marker, when it takes edits: exactly when it could be dragged. */
  private findEditableStart(target: HTMLElement): { row: TimelineRow; task: Task } | null {
    const routeUid = target.getAttribute('data-route-uid');
    const row = this.computedRows().find((candidate) => candidate.uid === routeUid);
    const task = row?.tasks.find((candidate) => candidate.kind === 'start');
    return row && task && this.edit.canDragStart(row, task) ? { row, task } : null;
  }

  private readonly translateFn = (key: string, params?: Record<string, unknown>) =>
    this.translate.instant(key, params);

  /** The row and the service block of a stop: a stop also draws its setup and wait steps. */
  private findStop(actionUid: string | null): { row: TimelineRow; task: Task } | null {
    if (!actionUid) return null;
    for (const row of this.computedRows()) {
      const task = row.tasks.find(
        (candidate) => candidate.actionUid === actionUid && candidate.kind === 'work'
      );
      if (task) return { row, task };
    }
    return null;
  }

  /** A stop takes menu edits exactly when it could be dragged. */
  private buildStopEditEntries(target: HTMLElement): ContextMenuOption[] {
    const stop = this.findStop(target.getAttribute('data-action-uid'));
    if (!stop || !this.edit.canDrag(stop.row, stop.task)) return [];
    return stopMenuEntries({
      ...stop,
      pins: this.shownPins(),
      dispatch: (gesture) =>
        this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture })),
      openForm: (request) => this.editForms.open(target, request),
      startPlacing: (carried, name) => this.edit.startPlacing(carried, name),
      translate: this.translateFn
    });
  }

  private buildRouteEditEntries(
    row: TimelineRow,
    target: HTMLElement
  ): ContextMenuOption[] {
    return routeMenuEntries({
      row,
      rows: this.computedRows(),
      dispatch: (gesture) =>
        this.store.dispatch(PlanEditSessionActions.gestureRequested({ gesture })),
      openForm: (request) => this.editForms.open(target, request),
      chooseCompany: (current, onPick) =>
        this.watchEntitySelection(
          'company',
          current,
          buildPresetContext(TIMELINE_PRESET_SURFACE, 'plan_edit_company'),
          (company, effectRef) => {
            effectRef.destroy();
            onPick(company);
          }
        ),
      translate: this.translateFn
    });
  }

  private buildModificationOptions(rowUid: string): ContextMenuOption[] {
    const actions = this.timelineModActionsService.getActionsForRow(rowUid);
    if (actions.length > 0) {
      return actions.map((action) => this.buildActionMenuOption(action));
    }

    return this.timelineModActionsService
      .getDescriptorsForRow(rowUid)
      .map((descriptor) => this.buildDescriptorMenuOption(rowUid, descriptor));
  }

  private buildActionMenuOption(action: EntityModificationAction): ContextMenuOption {
    if (action.type === 'multi-entity-select') {
      return this.buildEntitySelectMenuOption(action);
    }
    const actionDisabled = action.disabled ?? false;

    return {
      icon: action.icon,
      label: this.optionsActionLabel(
        action.labelKey,
        actionDisabled,
        action.disabledReasonKey
      ),
      disabled: actionDisabled,
      action: () => {},
      subOptions: action.options.map((opt) =>
        this.buildSubOption(actionDisabled, opt, () =>
          this.applyModificationOption(action, opt)
        )
      )
    };
  }

  /** Appends the reason only when the item is disabled, so enabled items read plainly. */
  private optionsActionLabel(
    labelKey: string,
    disabled: boolean,
    disabledReasonKey?: string
  ): string {
    const base = this.translate.instant(labelKey);

    return disabled && disabledReasonKey
      ? `${base} — ${this.translate.instant(disabledReasonKey)}`
      : base;
  }

  private buildDescriptorMenuOption(
    rowUid: string,
    descriptor: TimelineModificationActionDescriptor
  ): ContextMenuOption {
    if (descriptor.type === 'multi-entity-select') {
      const disabled = descriptor.disabled ?? false;
      return {
        icon: descriptor.icon,
        label: this.entitySelectLabel(
          descriptor.labelKey,
          descriptor.currentValue ?? null,
          disabled,
          descriptor.disabledReasonKey
        ),
        disabled,
        action: () => {
          if (disabled) return;
          this.requestEntitySelection(rowUid, descriptor);
        }
      };
    }

    const actionDisabled = descriptor.disabled ?? false;

    return {
      icon: descriptor.icon,
      label: this.optionsActionLabel(
        descriptor.labelKey,
        actionDisabled,
        descriptor.disabledReasonKey
      ),
      disabled: actionDisabled,
      action: () => {},
      subOptions: descriptor.options.map((option) =>
        this.buildSubOption(actionDisabled, option, () =>
          this.requestModification({
            rowUid,
            actionId: descriptor.actionId,
            optionId: option.optionId
          })
        )
      )
    };
  }

  /**
   * Sub-options inherit the parent's disabled state because the context menu still opens a
   * disabled item's submenu on hover; without it a disabled action could still be applied.
   */
  private buildSubOption(
    actionDisabled: boolean,
    option: {
      icon?: string;
      labelKey: string;
      isCurrentValue: boolean;
      disabled?: boolean;
      disabledReasonKey?: string;
    },
    apply: () => void
  ): ContextMenuOption {
    const optDisabled =
      actionDisabled || option.isCurrentValue || (option.disabled ?? false);

    return {
      icon: option.icon,
      label: this.optionsActionLabel(
        option.labelKey,
        option.disabled ?? false,
        option.disabledReasonKey
      ),
      disabled: optDisabled,
      action: () => {
        if (optDisabled) return;
        apply();
      }
    };
  }

  private requestEntitySelection(
    rowUid: string,
    descriptor: TimelineModificationActionDescriptor
  ): void {
    this.watchEntitySelection(
      descriptor.targetEntityType!,
      descriptor.currentValue ?? null,
      buildPresetContext(TIMELINE_PRESET_SURFACE, descriptor.actionId),
      (newValue, effectRef) => {
        effectRef.destroy();
        this.requestModification({
          rowUid,
          actionId: descriptor.actionId,
          selectedUid: newValue
        });
      }
    );
  }

  /**
   * Opens the entity picker and hands a pick to `onPick`.
   *
   * The picker reports a pick by writing to the signal it was given and nothing else, so the
   * dialog closing with that signal untouched is the only trace a dismissed pick leaves. Both
   * endings destroy the effect: without the second one, every cancelled pick would leave a live
   * effect on the component injector until the timeline itself is destroyed.
   */
  private watchEntitySelection(
    targetEntityType: string,
    currentValue: string | null,
    presetContext: string | undefined,
    onPick: (newValue: string, effectRef: ReturnType<typeof effect>) => void
  ): void {
    const selectedValue = signal<string | string[] | null>(currentValue);
    const currentItems = currentValue ? [currentValue] : [];

    // Until the dialog has been asked to open, closed means "not open yet" rather than
    // "dismissed" — the effect below runs before the timeout fires.
    let requested = false;

    // Let the context menu close first, then open the dialog
    setTimeout(() => {
      this.entityAutoCompleteService.show(
        targetEntityType,
        selectedValue,
        currentItems,
        false,
        presetContext
      );
      requested = true;
    });

    const effectRef = effect(
      () => {
        const newValue = selectedValue();
        const isOpen = this.entityAutoCompleteService.isVisible();

        if (newValue !== null && newValue !== currentValue) {
          onPick(newValue as string, effectRef);
          return;
        }

        if (requested && !isOpen) {
          effectRef.destroy();
        }
      },
      { injector: this.injector }
    );
  }

  /**
   * Hands a write to whichever window holds the live modification actions.
   *
   * The command is fire-and-forget — every window receives it and only the one that can resolve
   * it acts — so a missing outcome is the sole evidence that none could. A popup outliving the
   * timeline that fed it these descriptors would otherwise answer a click with nothing at all.
   */
  private requestModification(command: {
    rowUid: string;
    actionId: string;
    optionId?: string;
    selectedUid?: string;
  }): void {
    const requesterId = this.replicator.windowId.id;
    this.store.dispatch(applyTimelineModification({ ...command, requesterId }));

    // This window resolves it itself and reports through the local notification below.
    if (
      !isNil(this.timelineModActionsService.resolveAction(command.rowUid, command.actionId))
    ) {
      return;
    }

    this.actions$
      .pipe(
        ofType(timelineModificationOutcome),
        filter((outcome) => outcome.requesterId === requesterId),
        take(1),
        timeout(MODIFICATION_OUTCOME_TIMEOUT_MS),
        takeUntil(this._stop$)
      )
      .subscribe({
        error: () =>
          this.notificationService.error(
            this.translate.instant(
              'visualization.timeline.context_menu.modification_unanswered_title'
            ),
            this.translate.instant(
              'visualization.timeline.context_menu.modification_unanswered_message'
            )
          )
      });
  }

  private listenToModificationCommands(): void {
    this.actions$
      .pipe(ofType(applyTimelineModification), takeUntil(this._stop$))
      .subscribe(({ rowUid, actionId, optionId, selectedUid, requesterId }) => {
        const action = this.timelineModActionsService.resolveAction(rowUid, actionId);
        if (isNil(action)) return;
        if (this.planHeld()) {
          this.reportOutcome(
            'warning',
            this.translate.instant('features.plan.edit.held.write_refused_title'),
            this.translate.instant('features.plan.edit.held.write_disabled'),
            requesterId
          );
          return;
        }

        if (!isNil(selectedUid)) {
          this.applyCascadedSelection(action, selectedUid, undefined, requesterId);
          return;
        }

        const option = isNil(optionId)
          ? undefined
          : this.timelineModActionsService.resolveOption(action, optionId);
        if (isNil(option)) return;

        this.applyModificationOption(action, option, requesterId);
      });
  }

  /**
   * Reports a write to the window whose user asked for it. A write this window's own menu
   * started is nobody else's business: broadcasting it unaddressed would pop the same toast in
   * every open popup.
   */
  private reportOutcome(
    level: 'success' | 'warning' | 'error',
    title: string,
    message: string,
    requesterId?: string
  ): void {
    this.notificationService[level](title, message);

    if (isNil(requesterId) || requesterId === this.replicator.windowId.id) {
      return;
    }

    this.store.dispatch(
      timelineModificationOutcome({ level, title, message, requesterId })
    );
  }

  private buildTaskMenuOptions(target: HTMLElement): ContextMenuOption[] {
    const actionUid = target.getAttribute('data-action-uid');
    if (!actionUid) return [];

    const task = this.computedRows()
      .flatMap((row) => row.tasks)
      .find((candidate) => candidate.actionUid === actionUid);
    if (!task) return [];

    const options: ContextMenuOption[] = [
      {
        icon: ICONS.copy,
        label: this.translate.instant('common.copy_name'),
        action: () => navigator.clipboard.writeText(task.name)
      },
      {
        icon: ICONS.copy,
        label: this.translate.instant('common.copy_uid'),
        action: () => navigator.clipboard.writeText(actionUid)
      }
    ];

    if (task.kind === 'work' && this.isRouteActionCommentable()) {
      options.push({
        icon: ICONS.chatCircle,
        label: this.translate.instant('visualization.timeline.step_detail.open_comments'),
        action: () => this.openStepPopup(task, target)
      });
    }

    return options;
  }

  private applyModificationOption(
    action: EntityModificationAction,
    option: EntityModificationOption,
    requesterId?: string
  ): void {
    const prefix = 'visualization.timeline.context_menu.lock_update_';
    const keys = action.messageKeys;
    const successTitle = keys?.successTitle ?? `${prefix}success_title`;
    const successMessage = keys?.successMessage ?? `${prefix}success_message`;
    const errorTitle = keys?.errorTitle ?? `${prefix}error_title`;
    const errorMessage = keys?.errorMessage ?? `${prefix}error_message`;

    this.entityModificationService
      .applyModification(action, option)
      .pipe(take(1))
      .subscribe({
        next: () =>
          this.reportOutcome(
            'success',
            this.translate.instant(successTitle),
            this.translate.instant(successMessage),
            requesterId
          ),
        error: () =>
          this.reportOutcome(
            'error',
            this.translate.instant(errorTitle),
            this.translate.instant(errorMessage),
            requesterId
          )
      });
  }

  /** Shared by the live-action and descriptor menus, which must read identically. */
  private entitySelectLabel(
    labelKey: string,
    currentValue: string | null,
    disabled: boolean,
    disabledReasonKey?: string
  ): string {
    const currentLabel = currentValue ?? this.translate.instant('common.none');
    const base = `${this.translate.instant(labelKey)}: ${currentLabel}`;

    return disabled && disabledReasonKey
      ? `${base} — ${this.translate.instant(disabledReasonKey)}`
      : base;
  }

  private buildEntitySelectMenuOption(action: EntityModificationAction): ContextMenuOption {
    const disabled = action.disabled ?? false;

    return {
      icon: action.icon,
      label: this.entitySelectLabel(
        action.labelKey,
        action.currentValue ?? null,
        disabled,
        action.disabledReasonKey
      ),
      disabled,
      action: () => {
        if (disabled) return;
        this.openEntitySelectDialog(action);
      }
    };
  }

  private openEntitySelectDialog(action: EntityModificationAction): void {
    this.watchEntitySelection(
      action.targetEntityType!,
      action.currentValue ?? null,
      buildPresetContext(TIMELINE_PRESET_SURFACE, action.labelKey),
      (newValue, effectRef) => this.applyCascadedSelection(action, newValue, effectRef)
    );
  }

  private applyCascadedSelection(
    action: EntityModificationAction,
    newValue: string,
    effectRef?: ReturnType<typeof effect>,
    requesterId?: string
  ): void {
    const keys = action.messageKeys;
    if (isNil(action.cascade) || isNil(keys)) {
      // An action reaching this path without a cascade would write nothing, and without its
      // own keys cannot be reported on. Refuse it visibly instead of claiming success.
      effectRef?.destroy();
      this.reportOutcome(
        'error',
        this.translate.instant('common.error'),
        this.translate.instant('common.form_errors.update_failed'),
        requesterId
      );
      return;
    }

    // Everything here runs synchronously inside the selection effect, subscription included:
    // EntityModificationService resolves its transformer, dataspace and plan from signals while
    // the write pipeline is being built, so a tracked read would make those slices effect
    // dependencies. One of them changing while the writes are in flight would re-run the effect
    // — the selected value is unchanged, so the guard above lets it through — and apply the
    // whole cascade, repair pass included, a second time.
    untracked(() => this.runCascade(action, newValue, effectRef, keys, requesterId));
  }

  private runCascade(
    action: EntityModificationAction,
    newValue: string,
    effectRef: ReturnType<typeof effect> | undefined,
    keys: NonNullable<EntityModificationAction['messageKeys']>,
    requesterId?: string
  ): void {
    const scope = this.modificationScope();

    this.cascadeService.setContext(scope.dataSpaceId, scope.planFilter);

    this.cascadeService
      .resolveAndApply$(action, newValue)
      .pipe(
        take(1),
        switchMap((result) =>
          this.repairRegistrations$(result, scope).pipe(
            map((repair) => ({ result, repair }))
          )
        ),
        take(1),
        // Covers completion without emission too, so the effect can never outlive its dialog.
        finalize(() => effectRef?.destroy())
      )
      .subscribe({
        next: ({ result, repair }) => {
          if (result.status === 'aborted') {
            this.reportOutcome(
              'warning',
              this.translate.instant(keys.errorTitle),
              this.translate.instant(result.reasonKey, result.params),
              requesterId
            );
            return;
          }

          const repairMessage = this.repairMessage(repair);

          // The pass runs for a partial cascade too, so its outcome joins that message rather
          // than being dropped: the registrations were still reviewed and possibly written.
          if (result.status === 'partial') {
            this.reportOutcome(
              'warning',
              this.translate.instant(keys.partialTitle),
              [this.translate.instant(keys.partialMessage), repairMessage]
                .filter((part) => part.length > 0)
                .join(' '),
              requesterId
            );
            return;
          }

          const message = [this.translate.instant(keys.successMessage), repairMessage]
            .filter((part) => part.length > 0)
            .join(' ');

          const level =
            isNil(repair) || (repair.unrepaired.length === 0 && !repair.incomplete)
              ? 'success'
              : 'warning';

          this.reportOutcome(
            level,
            this.translate.instant(keys.successTitle),
            message,
            requesterId
          );
        },
        error: () => {
          this.reportOutcome(
            'error',
            this.translate.instant(keys.errorTitle),
            this.translate.instant(keys.errorMessage),
            requesterId
          );
        }
      });
  }

  /** Fetch and write scope of a modification, snapshotted so every stage of it agrees. */
  private modificationScope(): ModificationScope {
    const planId = this.planId();
    return {
      dataSpaceId: this.dataSpaceState()?.dataspace?.id,
      planFilter: isNil(planId)
        ? undefined
        : { id: planId, operator: PlanFilterOperator.IN }
    };
  }

  /**
   * The pass only runs for a write that actually moved a resource, and its failures are folded
   * into the notification: the route write it follows has already landed.
   */
  private repairRegistrations$(
    result: CascadeResult,
    scope: ModificationScope
  ): Observable<RegistrationRepairOutcome | undefined> {
    if (result.status === 'aborted' || isNil(result.repairScope)) return of(undefined);

    return this.registrationRepairService.repair$(
      result.repairScope,
      result.fields ?? {},
      scope.dataSpaceId,
      scope.planFilter
    );
  }

  private repairMessage(repair: RegistrationRepairOutcome | undefined): string {
    if (isNil(repair)) return '';

    const parts: string[] = [];
    if (repair.repaired > 0) {
      parts.push(
        this.translate.instant(`${REASSIGN_MESSAGE_PREFIX}repair_repaired`, {
          count: repair.repaired
        })
      );
    }
    if (repair.unrepaired.length > 0) {
      // One (route, kind) pair per slot the planner could not fill, so a route short of both a
      // vehicle and a trailer slot appears twice here — count routes, not pairs, and collapse a
      // resource's clause to one occurrence no matter how many routes it turned away.
      const seenResourceClauses = new Set<string>();
      const details = repair.unrepaired
        .filter((slot) => {
          const clauseKey = `${slot.kind}:${slot.resourceUid}`;
          if (seenResourceClauses.has(clauseKey)) return false;
          seenResourceClauses.add(clauseKey);
          return true;
        })
        .map((slot) => {
          const kind = this.translate.instant(
            `${REASSIGN_MESSAGE_PREFIX}repair_kind_${slot.kind}`
          );
          const key =
            slot.totalSlots === 0 ? 'repair_detail_unresolved' : 'repair_detail_saturated';
          return this.translate.instant(`${REASSIGN_MESSAGE_PREFIX}${key}`, {
            kind,
            resource: slot.resourceUid,
            total: slot.totalSlots
          });
        })
        .join(', ');
      const distinctRouteCount = new Set(repair.unrepaired.map((slot) => slot.routeUid))
        .size;
      parts.push(
        this.translate.instant(`${REASSIGN_MESSAGE_PREFIX}repair_unrepaired`, {
          count: distinctRouteCount,
          details
        })
      );
    }
    if (repair.incomplete) {
      parts.push(this.translate.instant(`${REASSIGN_MESSAGE_PREFIX}repair_incomplete`));
    }

    return parts.join(' ');
  }

  private loadTemperatures(uncachedUids: string[]) {
    const planId = this.planId();
    const dataSpaceId = this.dataSpaceState()?.dataspace?.id;

    if (isNil(dataSpaceId) || uncachedUids.length === 0) {
      return [];
    }

    const planFilter = isNil(planId)
      ? undefined
      : {
          id: planId,
          operator: PlanFilterOperator.IN
        };

    return this.selectorsService
      .selectData$<UserTemperatureV1Model>({
        entityType: 'TEMPERATURE',
        planFilter,
        dataSpaceId,
        entityTypeVersion: 'UserTemperature'
      })
      .pipe(
        take(1),
        map((res) => {
          this.temperatureByUid.update((cache) => {
            const newCache = { ...cache };
            for (const temp of res.result) {
              if (uncachedUids.includes(temp.uid)) {
                newCache[temp.uid] = temp.temperature_celsius;
              }
            }
            return newCache;
          });

          return res.result;
        })
      );
  }

  private getNowTimeStepPosition(): string | null {
    if (!this.isInitialized() || this.nbOfTimeSteps() === 0) {
      return null;
    }

    const {
      startDate,
      endDate,
      nbTimeStepsPerDayPos,
      nbOfTimeSteps: dataNbOfTimeSteps
    } = this.timelineState();
    const nowTimeStep = TimelineUtils.dateToStep(
      this.nowDate(),
      startDate,
      endDate,
      nbTimeStepsPerDayPos
    );

    if (nowTimeStep > dataNbOfTimeSteps || nowTimeStep < 0) {
      return null;
    }

    return `${CELL_LEGEND_WIDTH + nowTimeStep * (this.timelineWidth() / this.nbOfTimeSteps()) - 6}px`;
  }

  private getOptimalTimeStep(shown: TimelineRow[], availableWidth: number): number {
    const rows = spannedRows(shown);
    if (rows.length === 0 || availableWidth <= 0) {
      return 4;
    }

    const firstDateTime = rows.reduce(
      (t, row) => Math.min(t, row.startDate.getTime()),
      1e14
    );
    const lastDateTime = rows.reduce((t, row) => Math.max(t, row.endDate.getTime()), 0);

    const totalSpanInMs = lastDateTime - firstDateTime;
    const totalSpanInMin = totalSpanInMs / (60 * 1000);

    const maxTimeStepsVisible = Math.floor(availableWidth / CELL_MIN_WIDTH);
    const targetMaxTimeSteps = maxTimeStepsVisible * 2;

    // Find the optimal timestep that:
    // 1. Keeps total timesteps <= targetMaxTimeSteps (50% visibility goal)
    // 2. Provides reasonable granularity for the data
    let optimalTimeStep = this.timesteps.length - 1;

    for (let i = 0; i < this.timesteps.length; i++) {
      const stepDurationMin = TIME_STEP_DURATION_IN_MN[i];
      const nbTimeSteps = Math.ceil(totalSpanInMin / stepDurationMin);

      if (nbTimeSteps <= targetMaxTimeSteps) {
        const totalTaskDuration = rows.reduce((acc, row) => {
          return acc + (row.endDate.getTime() - row.startDate.getTime());
        }, 0);
        const avgTaskDurationMin = totalTaskDuration / rows.length / (60 * 1000);

        const avgTaskTimeSteps = avgTaskDurationMin / stepDurationMin;

        if (avgTaskTimeSteps >= 3 || i === this.timesteps.length - 1) {
          optimalTimeStep = i;
          break;
        }
      }
    }

    return optimalTimeStep;
  }
}
